/**
 * The primitives layer: the components an application gets for free, and the
 * last layer that knows nothing about what the application is.
 *
 * **This file is the portability seam.** Everything below it (`tokens.ts`,
 * `base.ts`) and everything in it is copyable into any application; everything
 * above it — `src/ui/style.ts` and its siblings — is this game. The test for
 * whether a rule belongs here is not "is it reusable", which is an opinion, but
 * **does its selector name a thing that exists outside this application**: a
 * panel, a row, a chip, a meter, a key cap. `.hand`, `.rhythm`, `.status` and
 * `.room` name a weapon, a combo timer, an affliction and a piece of level
 * geometry, and none of them may appear here.
 * `tests/designSystem.test.ts` enforces exactly that, against a vocabulary
 * list, in both directions.
 *
 * Ten components, and they are all of them:
 *
 *   layer      `.ui-layer` `.ui-clickable`   — a full-bleed overlay plane
 *   utility    `.ui-dim` … `.ui-caps`        — the one-property helpers
 *   ramp       `.ui-rar`                     — a categorical colour ramp
 *   meter      `.bar`                        — a 0..1 fill
 *   swatch     `.ui-swatch`                  — a sample of a colour, as a box
 *   chip       `.chip`                       — a small bordered noun
 *   key cap    `.ui-key`                     — a keyboard key, as a glyph
 *   dialog     `.scrim` `.panel`             — a thing in front of the page
 *   tabs       `.tabs`                       — one-of-N along an edge
 *   list       `.col` `.scroll` `.row`       — a scrolling column of rows
 *
 * Class names are unprefixed, which is the one thing to think about before
 * dropping this into an existing application — `.row` and `.panel` are common
 * names. docs/28 §6.2 has the two ways out.
 */

/**
 * Two notes that apply to the whole file, because they are the design and not
 * the implementation.
 *
 * **Elevation is a border and a surface step, never a blur.** There is no
 * shadow scale in this system and adding one would be a different design. A box
 * that needs to read as raised takes `--ui-line-hot` instead of `--ui-line`,
 * and a surface that needs to read as on top of another takes the next
 * `--ui-panel-N`. Three steps, and there is no fourth on purpose: past three,
 * the honest answer is a border. The single exception is `--ui-mark`, a 2px
 * inset rule on the leading edge, and it marks rather than lifts.
 *
 * **Colour is the second half of a signal, never the whole of it.** A status is
 * a countable glyph in a bordered box that *also* takes a colour; a hazard
 * escalates by adding chevrons and *also* goes red; a map room carries a glyph
 * as well as a border colour. The rule comes from docs/01 §6.4 — three
 * statuses at once must stay countable — and it is the reason `.ui-good` and
 * `.ui-bad` are text colours you add to something that already reads, rather
 * than backgrounds that carry the message alone.
 */
export const PRIMITIVES_CSS = /* css */ `
/* ---- layer: a full-bleed plane inside the root, for stacking overlays ---- */
/* The host decides pointer behaviour on the root's direct children, so
   passivity is set by the mounting code rather than fought over here. */
.ui-layer { position: absolute; inset: 0; }
.ui-clickable { pointer-events: auto; }

/* ---- utilities: one property each, and never more ---- */
/* If a helper grows a second property it has stopped being a utility and has
   become a component that has not been named yet. */
.ui-dim  { color: var(--ui-fg-dim); }
.ui-mute { color: var(--ui-fg-mute); }
.ui-good { color: var(--ui-good); }
.ui-bad  { color: var(--ui-bad); }
.ui-warn { color: var(--ui-warn); }
.ui-nowrap { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ui-grow { flex: 1 1 auto; min-width: 0; }
.ui-row { display: flex; align-items: center; gap: var(--ui-gap); }
/* The heading treatment of the entire system: uppercase, widely tracked, small.
   Tracking is what makes small caps readable; caps at normal tracking are a
   solid block. */
.ui-caps { text-transform: uppercase; letter-spacing: .16em; font-size: var(--ui-fs-label); }

/* ---- ramp: a categorical colour, chosen by a data attribute ---- */
/* Attribute-driven rather than one class per step, so the markup carries the
   *value* and the sheet owns the mapping. Renaming a step is one edit here and
   one in the data, and never a third somewhere in the middle. */
.ui-rar { color: var(--ui-r-common); }
.ui-rar[data-rarity="uncommon"] { color: var(--ui-r-uncommon); }
.ui-rar[data-rarity="rare"]     { color: var(--ui-r-rare); }
.ui-rar[data-rarity="epic"]     { color: var(--ui-r-epic); }
.ui-rar[data-rarity="relic"]    { color: var(--ui-r-relic); }
.ui-rar[data-rarity="cursed"]   { color: var(--ui-r-cursed); }

/* ---- meter: a 0..1 fill, written as --f on the element ---- */
/* Transform-driven: scaleX is compositor-only, width is layout. At 60 Hz with a
   dozen meters live that difference is the whole per-frame UI budget. Set --f
   rather than a width, and set it only when the quantised value actually moved.
   --c overrides the fill colour; leaving it unset gives the dim ink, which is
   the right default for a meter nobody has assigned a meaning to. */
.bar { position: relative; height: 6px; border: 1px solid var(--ui-line); background: var(--ui-well); overflow: hidden; }
.bar > i { position: absolute; inset: 0; transform-origin: 0 50%; transform: scaleX(var(--f, 0)); background: var(--c, var(--ui-fg-dim)); }

/* ---- swatch: a sample of a colour, as a box ---- */
/* The colour is data, so it arrives as \`--c\` — the same property name the meter's
   fill takes, because "the colour this element is showing, decided by something
   other than the sheet" is one idea and deserves one spelling. \`--sz\` is the
   edge, defaulting to the label type's size so a swatch set inline with a row of
   text is the height of the words beside it.

   It is here rather than in an application because a box of a colour is not a
   thing any one application owns: this system's own gallery drew one with five
   inline styles, and the editor in \`tools/\` drew three more at 20px, 12px and
   10px. Four hand-tuned copies of one idea is the count docs/28 §6.3 says makes
   a concept rather than a coincidence. */
.ui-swatch {
  display: inline-block; flex: none;
  width: var(--sz, var(--ui-fs-label)); height: var(--sz, var(--ui-fs-label));
  border: 1px solid var(--ui-line); background: var(--c, var(--ui-cell));
}

/* ---- chip: a small bordered noun, inline with text ---- */
.chip {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--ui-line); background: var(--ui-panel-2);
  padding: 0 4px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); white-space: nowrap;
}

/* ---- dialog: a scrim, and a thing in front of the page ---- */
/* The scrim is a sibling of the panel, not its parent: a panel that is a child
   of its own backdrop inherits the backdrop's opacity, which is how dimmed
   dialogs happen. */
.scrim { position: absolute; inset: 0; background: var(--ui-scrim); }
.panel {
  position: absolute; inset: 20px;
  display: flex; flex-direction: column;
  background: var(--ui-panel); border: 1px solid var(--ui-line-hot);
}
.panel__head { display: flex; align-items: center; gap: 12px; padding: 6px 10px; border-bottom: 1px solid var(--ui-line); background: var(--ui-panel-2); }
.panel__title { letter-spacing: .3em; text-transform: uppercase; color: var(--ui-accent); }
.panel__body { flex: 1 1 auto; display: flex; min-height: 0; }
.panel__foot { display: flex; align-items: center; gap: 10px; padding: 4px 10px; border-top: 1px solid var(--ui-line); background: var(--ui-panel-2); font-size: var(--ui-fs-label); color: var(--ui-fg-mute); }

/* ---- key cap: a keyboard key, printed as the button and not as the verb ---- */
/* The bare-element selector is how a footer full of <kbd> gets this for free;
   .ui-key is how anything else asks for it. One rule, two ways in, so the two
   cannot drift — which is the failure this system has already had four times
   in TypeScript (docs/25 §5.2) and twenty-three times in CSS (docs/28 §5). */
.ui-key, .panel__foot kbd { border: 1px solid var(--ui-line-hot); padding: 0 3px; color: var(--ui-fg-dim); }

/* ---- tabs: one-of-N along an edge ---- */
/* gap:0 and a shared border: tabs are a strip, not a row of buttons, and the
   1px between two of them would double up without the border-right dance. */
.tabs { display: flex; gap: 0; }
.tabs > button { border-right: none; text-transform: uppercase; letter-spacing: .16em; font-size: var(--ui-fs-label); padding: 3px 10px; color: var(--ui-fg-dim); }
.tabs > button:last-child { border-right: 1px solid var(--ui-line); }
.tabs > button[data-on="1"] { color: var(--ui-accent); border-color: var(--ui-line-hot); background: var(--ui-panel-3); }

/* ---- list: a scrolling column of rows ---- */
/* min-height:0 on the column is load-bearing and is the single most common
   reason a flex scroller does not scroll: without it the column refuses to
   shrink below its content and the overflow happens to the page instead. */
.col { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.col__head { padding: 4px 8px; border-bottom: 1px solid var(--ui-line); color: var(--ui-fg-mute); text-transform: uppercase; letter-spacing: .18em; font-size: var(--ui-fs-label); }
.scroll { overflow-y: auto; overflow-x: hidden; flex: 1 1 auto; min-height: 0; scrollbar-width: thin; scrollbar-color: var(--ui-line-hot) transparent; }
.scroll::-webkit-scrollbar { width: 8px; }
.scroll::-webkit-scrollbar-thumb { background: var(--ui-line-hot); }

/* A row is a hit target, a separator and three states: hovered, under the
   cursor, and dimmed. Hover and cursor are different things and both are
   needed — a keyboard cursor that vanishes because the mouse is elsewhere is
   how a list stops being navigable without a mouse. */
.row {
  display: flex; align-items: center; gap: 6px;
  padding: 2px 8px; border-bottom: 1px solid var(--ui-line-soft); cursor: pointer;
}
.row:hover { background: var(--ui-panel-2); }
.row[data-cursor="1"] { background: var(--ui-panel-3); box-shadow: var(--ui-mark); }
.row[data-dim="1"] { opacity: .4; }
.row__glyph { width: 16px; text-align: center; }
.row__acts { display: flex; gap: 3px; }
.row__acts button { font-size: var(--ui-fs-tiny); padding: 0 4px; letter-spacing: .1em; }
`;
